import { itemizedCapexFromBom } from './itemizedCapex';
import {
  PHASE_DERIVED_CAMPUS_FIELDS,
  applyCampusPatch,
  deriveCapexPerKwUsd,
  sortFinancialPhases,
  writeFinancialAssumptions,
} from '../types/financialAssumptions';
import { phaseIncrementMW } from './projectIntent';
import type {
  ApplyCampusPatchResult,
  FieldProvenance,
  FinancialAssumptions,
  FinancialPhase,
  FinancialTechnology,
  PhaseDerivedCampusField,
} from '../types/financialAssumptions';
import type { PowerSourceKey, ProjectPhase } from '../types/siteRag';
import type { BomItem, SupplyChainPlan } from '../types/supplyChain';
import { bomGenerationCapacityMw, selectActiveBomItems } from '../types/supplyChain';

/** Unit conversion — 1 MW = 1000 kW (named per CLAUDE.md §4, no magic numbers). */
const KW_PER_MW = 1000;

/** Which capacity basis the BoM→CAPEX $/kW divisor came from. */
export type CapexCapacityBasis = 'bom-generation' | 'manual-capacity' | 'none';

/**
 * Map a `ProjectPhase.powerSource` → a concrete `FinancialTechnology` bucket
 * for per-phase technology seeding. Returns `undefined` (→ inherit the campus
 * technology) whenever the intent source does NOT map 1:1 to a bucket, so the
 * financial phase is never given a fabricated OR mislabeled value.
 *
 * `large-nuclear` deliberately returns undefined: the FinancialTechnology union
 * has no large-nuclear bucket, and substituting 'smr' would apply SMR-specific
 * outlier $/kW bands (industryBaselines) and BoM templates to a fundamentally
 * different technology — a silent misclassification. Inheriting the campus tech
 * (which the user sets explicitly) is the honest, non-fabricating choice.
 */
export function powerSourceToFinancialTechnology(
  source: PowerSourceKey,
): FinancialTechnology | undefined {
  switch (source) {
    case 'smr':           return 'smr';
    case 'gas-cc':        return 'gas-cc';
    case 'gas-sc':        return 'gas-sc';
    case 'solar-bess':    return 'solar-bess';
    case 'wind-bess':     return 'wind-bess';
    // large-nuclear / grid / hybrid / undecided → no faithful bucket → inherit campus.
    default:              return undefined;
  }
}

export interface SyncCapexFromBomResult {
  capexTotalUsd: number;
  capexPerKwUsd: number | null;
  pricedItemCount: number;
  unpricedItemCount: number;
  /**
   * BOM-CAPEX (#28) — the capacity (MW) used as the $/kW divisor and where it
   * came from. `bom-generation` = Σ(qty × powerPerUnitMw) over generation
   * lines (preferred when present); `manual-capacity` = the FA-supplied
   * `capacityMW` fallback; `none` = no capacity basis (capexPerKwUsd is null).
   */
  capacityBasisMw: number | null;
  capacityBasis: CapexCapacityBasis;
}

export interface SyncCommercialTermsFromBomResult {
  depositTotalUsd: number;
  depositShareOfCapex: number | null;
  itemsWithDepositCount: number;
  averageValidityDays: number | null;
  paymentTermsSummary: string[];
  freightTermsSummary: string[];
}

/**
 * Compute FA patch from BoM — does not persist.
 *
 * CAPEX (`capexTotalUsd`) is the sum of each line's extended price
 * (`totalCostUsd` = quantity × unitCostUsd) across priced lines — so a 50-unit
 * generation line at $1.6M/unit contributes $80M, not $1.6M.
 *
 * CAPACITY BASIS (#28): the $/kW divisor prefers the BOM-DERIVED generation
 * capacity Σ(qty × powerPerUnitMw) when ANY generation line declares a
 * power-per-unit (`bomGenerationCapacityMw`). Only when the BoM declares no
 * generation power does it fall back to the manually-supplied `capacityMW`
 * (FinancialAssumptions). This keeps $/kW self-consistent with the BoM that
 * produced the CAPEX, and never double-counts: cost comes from `totalCostUsd`,
 * capacity from `powerPerUnitMw` — two independent fields on the same line.
 *
 * Worked example: 50 × $1.6M = $80M CAPEX; 50 × 2 MW = 100 MW;
 * $/kW = $80,000,000 / (100 × 1000 kW) = $800/kW.
 */
export function computeCapexSyncFromBom(
  plan: SupplyChainPlan | null | undefined,
  capacityMW: number | null | undefined,
): SyncCapexFromBomResult | null {
  const itemized = itemizedCapexFromBom(plan);
  if (itemized.pricedItemCount === 0) return null;

  // Prefer BoM-derived generation capacity; fall back to the manual FA value.
  const bomCapacityMw = bomGenerationCapacityMw(plan);
  let capacityBasisMw: number | null = null;
  let capacityBasis: CapexCapacityBasis = 'none';
  if (bomCapacityMw != null && bomCapacityMw > 0) {
    capacityBasisMw = bomCapacityMw;
    capacityBasis = 'bom-generation';
  } else if (capacityMW != null && Number.isFinite(capacityMW) && capacityMW > 0) {
    capacityBasisMw = capacityMW;
    capacityBasis = 'manual-capacity';
  }

  const capexPerKwUsd =
    capacityBasisMw != null && capacityBasisMw > 0
      ? itemized.total / (capacityBasisMw * KW_PER_MW)
      : null;
  return {
    capexTotalUsd: itemized.total,
    capexPerKwUsd,
    pricedItemCount: itemized.pricedItemCount,
    unpricedItemCount: itemized.unpricedItemCount,
    capacityBasisMw,
    capacityBasis,
  };
}

/** Compute finance-facing procurement terms summary from BoM + quote data. */
export function computeCommercialTermsSyncFromBom(
  plan: SupplyChainPlan | null | undefined,
  capexTotalUsd: number | null | undefined,
): SyncCommercialTermsFromBomResult | null {
  if (!plan || plan.bom.length === 0) return null;
  const awardedByBom = new Map(
    plan.quotes
      .filter(q => q.status === 'awarded')
      .map(q => [q.bomItemId, q] as const),
  );
  let depositTotalUsd = 0;
  let itemsWithDepositCount = 0;
  let validityDaysTotal = 0;
  let validityCount = 0;
  const paymentTerms = new Set<string>();
  const freightTerms = new Set<string>();
  for (const item of plan.bom) {
    const awarded = awardedByBom.get(item.id);
    const deposit = awarded?.depositAmountUsd ?? item.baselineDepositAmountUsd ?? null;
    if (deposit != null && Number.isFinite(deposit) && deposit > 0) {
      depositTotalUsd += deposit;
      itemsWithDepositCount += 1;
    }
    const validityDays = awarded?.validityDays ?? item.baselineQuoteValidityDays ?? null;
    if (validityDays != null && Number.isFinite(validityDays) && validityDays > 0) {
      validityDaysTotal += validityDays;
      validityCount += 1;
    }
    const payment = awarded?.paymentTerms ?? item.baselinePaymentTerms;
    if (payment) paymentTerms.add(payment.trim());
    const freight = awarded?.freightTerms ?? item.baselineFreightTerms;
    if (freight) freightTerms.add(freight.trim());
  }
  return {
    depositTotalUsd,
    depositShareOfCapex:
      capexTotalUsd != null && capexTotalUsd > 0
        ? depositTotalUsd / capexTotalUsd
        : null,
    itemsWithDepositCount,
    averageValidityDays: validityCount > 0 ? validityDaysTotal / validityCount : null,
    paymentTermsSummary: Array.from(paymentTerms).slice(0, 5),
    freightTermsSummary: Array.from(freightTerms).slice(0, 5),
  };
}

/** Result of the campus BoM→CAPEX sync — `unapplied` is NEVER discarded. */
export interface ApplyCapexSyncResult {
  next: FinancialAssumptions;
  /**
   * Rollup-derived campus fields the patch could not attribute to a phase
   * (multi-phase model). The caller MUST surface these and route the user to
   * the per-phase "Pull from BOM + Intent" path — silently dropping them is
   * the class of bug `applyCampusPatch` exists to end (audit A5/A12).
   */
  unapplied: PhaseDerivedCampusField[];
}

export function applyCapexSyncToFinancialAssumptions(
  fa: FinancialAssumptions,
  sync: SyncCapexFromBomResult,
  actor: string,
  now: string,
  commercial?: SyncCommercialTermsFromBomResult | null,
  /**
   * `supplyChainPlan.updatedAt` for the plan that produced `commercial`.
   * Stamped onto `procurementCommercials.updatedAt` so a LATER plan edit is
   * detectable as staleness (audit A5 / 1.2c) instead of the snapshot being
   * carried forward forever by `{...fa}` spreads. Falls back to `now` when the
   * caller has no plan timestamp.
   */
  planUpdatedAt?: string | null,
): ApplyCapexSyncResult {
  // PHASE-AWARE (fixed 2026-08-19). `phases[]` is authoritative on write, so
  // writing the BoM CAPEX onto the CORE alone was reverted by the next save
  // while the `derived / supplyChainPlan.bom` provenance chip survived — the
  // report then flipped between the BoM figure and the manual one depending on
  // whether the user had opened the form since the sync. `applyCampusPatch`
  // routes it onto the single phase; on a multi-phase model the campus sync
  // cannot attribute the figure and leaves the phases alone (the per-phase
  // `pullFinancialFromSources` path is the supported route there).
  const { next: phaseAligned, unapplied }: ApplyCampusPatchResult = applyCampusPatch(fa, {
    capexTotalUsd: sync.capexTotalUsd,
    capexPerKwUsd: sync.capexPerKwUsd,
  });
  const next: FinancialAssumptions = {
    ...phaseAligned,
    updatedAt: now,
    updatedBy: actor,
    fieldProvenance: {
      ...phaseAligned.fieldProvenance,
      capexTotalUsd: {
        source: 'derived',
        confidence: 'medium',
        cellReference: 'supplyChainPlan.bom',
        extractedAt: now,
      },
      ...(sync.capexPerKwUsd != null
        ? {
            capexPerKwUsd: {
              source: 'derived',
              confidence: 'medium',
              cellReference: 'supplyChainPlan.bom',
              extractedAt: now,
            },
          }
        : {}),
    },
    ...(commercial
      ? {
          procurementCommercials: {
            ...commercial,
            // The BoM the snapshot DESCRIBES, not the moment we wrote it —
            // that is what makes a later plan edit detectable as staleness.
            updatedAt: planUpdatedAt ?? now,
          },
        }
      : {}),
  };
  return { next, unapplied };
}

// ─────────────────────────────────────────────────────────────────────────────
// FA WRITE BOUNDARY — provenance hygiene (audit A5 / plan item 1.2b + 1.2c).
//
// `writeFinancialAssumptions` re-derives every campus rollup field from
// `phases[]`. When it does, a `fieldProvenance` entry still attached to one of
// those fields describes a value the record NO LONGER CONTAINS: the chip says
// "extracted from Inputs!B14" next to a number that is now Σ(phase CAPEX).
// A chip must never outlive its value, so this boundary re-stamps (or drops)
// exactly the campus stamps whose value the rollup changed. Stamps whose value
// SURVIVED the rollup are untouched — the BoM sync's `derived /
// supplyChainPlan.bom` chip keeps its cell reference.
//
// It also expires `procurementCommercials`: that snapshot is carried forward by
// every `{...fa}` spread forever, so the report's deposit sentence could
// describe a BoM that no longer exists. When the live plan is NEWER than the
// snapshot, the snapshot is dropped rather than shown as current.
// ─────────────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
// Financial auto-populate — per-phase BOM rollup + project-intent sync.
//
// INVESTMENT-GRADE HARD RULE: never fabricate or guess a financial value.
// Every field below is filled ONLY from a real, present source:
//   - BOM line items  (sum of BomItem.totalCostUsd, grouped by phaseId)
//   - project-intent phases (capacityMW, targetInServiceDate)
// A field with no real source stays null/blank. No industry defaults here.
// ─────────────────────────────────────────────────────────────────────────────

/** Provenance cell-reference for values rolled up from the BOM. */
export const BOM_ROLLUP_CELL_REFERENCE = 'supplyChainPlan.bom';

/** Per-phase CAPEX rollup, derived purely from priced BOM line items. */
export interface PerPhaseCapexResult {
  /** Aligns with the FinancialPhase.phaseId this rollup is for. */
  phaseId: string;
  /**
   * Sum of `BomItem.totalCostUsd` for items mapped to this phase — `null`
   * when the phase has no capacityMW OR has zero priced items (we refuse
   * to publish a CAPEX we cannot attribute to a real capacity / real price).
   */
  capexTotalUsd: number | null;
  /** capexTotalUsd / (capacityMW * 1000); `null` under the same guard. */
  capexPerKwUsd: number | null;
  /** Count of items mapped to this phase with a finite totalCostUsd. */
  pricedItemCount: number;
  /** Count of items mapped to this phase with a null/non-finite totalCostUsd. */
  unpricedItemCount: number;
}

/**
 * Group `BomItem`s by phase and roll up CAPEX per `FinancialPhase`.
 *
 * Grouping rule (documented + tested):
 *   - An item whose `phaseId` matches a phase in `phases` is bucketed there.
 *   - An item with an **undefined** `phaseId` (legacy single-phase BoMs) is
 *     bucketed into the EARLIEST phase, i.e. the one with the lowest
 *     `sequence` (Phase 1). This is the only safe attribution: undefined
 *     means "not yet phased", and the earliest phase is the one being built
 *     first, so its CAPEX is the least speculative place to attribute spend.
 *   - An item whose `phaseId` does NOT match any provided phase is dropped
 *     from every phase rollup (it cannot be attributed to a real phase, and
 *     fabricating an attribution would violate the investment-grade rule).
 *
 * For each phase the rollup is published ONLY when the phase has a positive
 * `capacityMW` AND at least one priced item; otherwise capexTotalUsd /
 * capexPerKwUsd are left `null`. pricedItemCount / unpricedItemCount are
 * always reported so the UI can explain why a phase was skipped.
 *
 * BOM-ROLLUP: mutually-exclusive procurement ALTERNATIVES (lines sharing an
 * `optionGroupId`) are collapsed to the ONE active option per group (via
 * `selectActiveBomItems`) BEFORE phase bucketing — so three rival Phase-1
 * prime movers @ $240M each roll into ONE phase as $240M, not their $720M sum.
 */
export function computePerPhaseCapexFromBom(
  plan: SupplyChainPlan | null | undefined,
  phases: readonly FinancialPhase[],
): PerPhaseCapexResult[] {
  if (phases.length === 0) return [];

  // Identify the earliest (lowest-sequence) phase as the undefined-phaseId bucket.
  const sorted = sortFinancialPhases([...phases]);
  const earliestPhaseId = sorted[0]?.phaseId;
  const knownPhaseIds = new Set(phases.map(p => p.phaseId));

  // Accumulators keyed by phaseId.
  const tally = new Map<string, { capex: number; priced: number; unpriced: number }>();
  for (const p of phases) tally.set(p.phaseId, { capex: 0, priced: 0, unpriced: 0 });

  const items: BomItem[] = Array.isArray(plan?.bom)
    ? selectActiveBomItems(plan!.bom)
    : [];
  for (const item of items) {
    // Resolve the target phase for this item.
    let targetPhaseId: string | undefined;
    if (item.phaseId == null) {
      targetPhaseId = earliestPhaseId; // undefined → earliest/Phase-1 bucket
    } else if (knownPhaseIds.has(item.phaseId)) {
      targetPhaseId = item.phaseId;
    } else {
      continue; // phaseId references a phase not in this set — un-attributable, drop
    }
    if (targetPhaseId == null) continue;
    const bucket = tally.get(targetPhaseId);
    if (!bucket) continue;

    if (item.totalCostUsd == null || !Number.isFinite(item.totalCostUsd)) {
      bucket.unpriced += 1;
      continue;
    }
    bucket.priced += 1;
    bucket.capex += item.totalCostUsd;
  }

  // Cumulative convention: a phase's BoM-derived CAPEX is the cost of the
  // equipment ADDED that phase (the increment), so the $/kW divisor must be
  // the per-phase INCREMENT MW, NOT the cumulative-at-phase capacity —
  // otherwise late phases (large cumulative, small increment) report an
  // artificially LOW $/kW. The increment is computed off the sorted-by-
  // sequence order (`sorted` above), keyed back by phaseId.
  const incrementByPhaseId = new Map<string, number | null>();
  sorted.forEach((p, i) => incrementByPhaseId.set(p.phaseId, phaseIncrementMW(sorted, i)));

  return phases.map((phase) => {
    const bucket = tally.get(phase.phaseId) ?? { capex: 0, priced: 0, unpriced: 0 };
    // The publishability guard stays on cumulative capacity (any phase with
    // a real cumulative figure is a real phase); only the $/kW DIVISOR uses
    // the increment.
    const hasCapacity =
      phase.capacityMW != null && Number.isFinite(phase.capacityMW) && phase.capacityMW > 0;
    const incrementMw = incrementByPhaseId.get(phase.phaseId) ?? null;
    const publishable = hasCapacity && bucket.priced >= 1;
    const capexTotalUsd = publishable ? bucket.capex : null;
    return {
      phaseId: phase.phaseId,
      capexTotalUsd,
      capexPerKwUsd: publishable
        ? deriveCapexPerKwUsd(bucket.capex, incrementMw)
        : null,
      pricedItemCount: bucket.priced,
      unpricedItemCount: bucket.unpriced,
    };
  });
}

/**
 * Map `projectIntent.phases[]` onto `FinancialPhase[]`, filling ONLY blank
 * financial fields from real intent values. Alignment is by `phaseId`
 * (`ProjectPhase.id` === `FinancialPhase.phaseId`).
 *
 * Real intent sources used (verified against `ProjectPhase` in siteRag.ts):
 *   - `ProjectPhase.capacityMW`          → FinancialPhase.capacityMW
 *   - `ProjectPhase.targetInServiceDate` → FinancialPhase.inServiceDate
 *
 * Fill discipline:
 *   - An existing financial phase keeps its value when already set; intent
 *     only fills a `null`/blank field.
 *   - Intent phases with no matching financial phase are appended as new
 *     financial phases (so a freshly-intent-defined phase shows up), with
 *     capex left null (no BOM rollup has run yet in this pure step).
 *   - Intent values that are themselves null/blank fill nothing.
 *   - The returned array is sorted by sequence.
 */
export function buildFinancialPhasesFromIntent(
  intentPhases: readonly ProjectPhase[] | null | undefined,
  existingPhases: readonly FinancialPhase[] | null | undefined,
): FinancialPhase[] {
  const existing = existingPhases ? [...existingPhases] : [];
  const intents = intentPhases ?? [];
  const byId = new Map(existing.map(p => [p.phaseId, p] as const));
  const maxSequence = existing.reduce((m, p) => Math.max(m, p.sequence), 0);

  const result: FinancialPhase[] = existing.map(p => ({ ...p }));
  let appendSequence = maxSequence;

  for (const ip of intents) {
    const target = byId.get(ip.id);
    const intentCapacity =
      ip.capacityMW != null && Number.isFinite(ip.capacityMW) ? ip.capacityMW : null;
    const intentDate = ip.targetInServiceDate ?? null;
    // Concrete tech bucket only — `undefined` for grid/hybrid/undecided so the
    // phase keeps inheriting the campus technology (never fabricate a value).
    const intentTech = powerSourceToFinancialTechnology(ip.powerSource);

    if (target) {
      const idx = result.findIndex(p => p.phaseId === target.phaseId);
      if (idx >= 0) {
        const current = result[idx]!;
        const filledCapacity = current.capacityMW ?? intentCapacity;
        result[idx] = {
          ...current,
          // Fill blanks only — never clobber an existing financial value.
          capacityMW: filledCapacity,
          inServiceDate: current.inServiceDate ?? intentDate,
          // Seed per-phase technology only when the phase has no override yet
          // and intent pins a concrete tech; otherwise leave it inheriting.
          technology: current.technology ?? intentTech,
          // Refresh derived $/kW only when capex is present and capacity was just filled.
          capexPerKwUsd:
            current.capexPerKwUsd
            ?? deriveCapexPerKwUsd(current.capexTotalUsd, filledCapacity),
        };
      }
      continue;
    }

    // New phase introduced by intent — append, sourcing only capacity + date
    // (+ a concrete technology when intent pins one).
    appendSequence += 1;
    result.push({
      phaseId: ip.id,
      label: ip.label,
      sequence: appendSequence,
      capacityMW: intentCapacity,
      capexTotalUsd: null,
      capexPerKwUsd: null,
      capacityFactorPct: null,
      inServiceDate: intentDate,
      constructionMonths: null,
      technology: intentTech,
      status: 'planned',
    });
  }

  return sortFinancialPhases(result);
}

/** Inputs to the pure aggregator. */
export interface FinancialPullSources {
  plan: SupplyChainPlan | null | undefined;
  intentPhases: readonly ProjectPhase[] | null | undefined;
  existing: readonly FinancialPhase[] | null | undefined;
}

/**
 * One filled (or skipped) field, addressed by phase + field name, for the
 * provenance map + review surfaces.
 */
export interface FilledFieldRef {
  phaseId: string;
  field: 'capacityMW' | 'capexTotalUsd' | 'capexPerKwUsd' | 'inServiceDate';
  source: 'bom-rollup' | 'project-intent';
}

export interface FinancialPullResult {
  /** Composed phases with blanks filled from real sources only. */
  phases: FinancialPhase[];
  /**
   * Provenance keyed by `${phaseId}:${field}`. Every entry uses source
   * 'derived' with cellReference 'supplyChainPlan.bom' (BOM rollup) or
   * 'projectIntent.phases[i]' (intent sync).
   */
  fieldProvenance: Record<string, FieldProvenance>;
  /** Fields that were auto-filled from a real source this run. */
  filledFields: FilledFieldRef[];
  /**
   * Fields left blank because no real source was present (documented so the
   * UI can show "no source — enter manually" rather than silently zeroing).
   */
  skippedUnsourced: Array<{ phaseId: string; field: FilledFieldRef['field'] }>;
}

const PROVENANCE_KEY = (phaseId: string, field: string): string => `${phaseId}:${field}`;

/**
 * Pure aggregator composing the per-phase BOM rollup + intent sync.
 *
 * Behaviour:
 *   1. Build the candidate phase set by filling blank capacity / in-service
 *      dates from intent (`buildFinancialPhasesFromIntent`).
 *   2. Roll up CAPEX per phase from the BOM (`computePerPhaseCapexFromBom`).
 *   3. For each phase, fill a financial field ONLY when:
 *        - the field is currently blank, OR
 *        - its existing provenance.source is 'derived' (safe to refresh) —
 *          a 'manual' field is NEVER clobbered.
 *      and a real source value exists.
 *   4. Stamp `FieldProvenance` for every field actually filled.
 *
 * `existingProvenance` carries the phase-keyed provenance from a prior pull
 * (keys `${phaseId}:${field}`) so we can tell 'derived' (refreshable) from
 * 'manual' (protected). Absent ⇒ treat all current values as user-authored
 * and only fill blanks.
 */
export function buildFinancialPullFromSources(
  sources: FinancialPullSources,
  now: string,
  existingProvenance?: Record<string, FieldProvenance>,
): FinancialPullResult {
  const { plan, intentPhases, existing } = sources;

  // Step 1 — intent fills blank capacity / in-service date, aligned by id.
  const intentFilled = buildFinancialPhasesFromIntent(intentPhases, existing);

  // Index of original existing phases (to know what was blank BEFORE intent).
  const original = new Map((existing ?? []).map(p => [p.phaseId, p] as const));
  const intentById = new Map((intentPhases ?? []).map((p, i) => [p.id, { p, i }] as const));

  // Step 2 — BOM rollup per (intent-filled) phase.
  const rollups = computePerPhaseCapexFromBom(plan, intentFilled);
  const rollupById = new Map(rollups.map(r => [r.phaseId, r] as const));

  const fieldProvenance: Record<string, FieldProvenance> = {};
  const filledFields: FilledFieldRef[] = [];
  const skippedUnsourced: Array<{ phaseId: string; field: FilledFieldRef['field'] }> = [];

  // Is a field safe to (re)write? Blank ⇒ yes. 'derived' provenance ⇒ refresh.
  // 'manual' (or any non-derived) ⇒ protected.
  const canWrite = (phaseId: string, field: string, currentValue: number | string | null): boolean => {
    if (currentValue == null) return true;
    const prov = existingProvenance?.[PROVENANCE_KEY(phaseId, field)];
    return prov?.source === 'derived';
  };

  const stamp = (
    phaseId: string,
    field: FilledFieldRef['field'],
    source: FilledFieldRef['source'],
    cellReference: string,
  ): void => {
    fieldProvenance[PROVENANCE_KEY(phaseId, field)] = {
      source: 'derived',
      confidence: 'medium',
      cellReference,
      extractedAt: now,
    };
    filledFields.push({ phaseId, field, source });
  };

  const phases: FinancialPhase[] = intentFilled.map((phase) => {
    const orig = original.get(phase.phaseId);
    const rollup = rollupById.get(phase.phaseId);
    const intentEntry = intentById.get(phase.phaseId);
    const next: FinancialPhase = { ...phase };

    // ── Intent-sourced fields: capacityMW + inServiceDate ──────────────────
    if (intentEntry) {
      const cellRef = `projectIntent.phases[${intentEntry.i}]`;
      const intentCapacity =
        intentEntry.p.capacityMW != null && Number.isFinite(intentEntry.p.capacityMW)
          ? intentEntry.p.capacityMW
          : null;
      const intentDate = intentEntry.p.targetInServiceDate ?? null;

      // capacity: fill only if originally blank (or derived) AND intent has a real value.
      if (intentCapacity != null && canWrite(phase.phaseId, 'capacityMW', orig?.capacityMW ?? null)) {
        if (orig?.capacityMW == null) {
          next.capacityMW = intentCapacity;
          stamp(phase.phaseId, 'capacityMW', 'project-intent', cellRef);
        }
      }
      if (intentDate != null && canWrite(phase.phaseId, 'inServiceDate', orig?.inServiceDate ?? null)) {
        if (orig?.inServiceDate == null) {
          next.inServiceDate = intentDate;
          stamp(phase.phaseId, 'inServiceDate', 'project-intent', cellRef);
        }
      }
      if (intentCapacity == null) {
        skippedUnsourced.push({ phaseId: phase.phaseId, field: 'capacityMW' });
      }
      if (intentDate == null && orig?.inServiceDate == null) {
        skippedUnsourced.push({ phaseId: phase.phaseId, field: 'inServiceDate' });
      }
    }

    // ── BOM-sourced fields: capexTotalUsd + capexPerKwUsd ──────────────────
    if (rollup && rollup.capexTotalUsd != null) {
      if (canWrite(phase.phaseId, 'capexTotalUsd', orig?.capexTotalUsd ?? null)) {
        next.capexTotalUsd = rollup.capexTotalUsd;
        stamp(phase.phaseId, 'capexTotalUsd', 'bom-rollup', BOM_ROLLUP_CELL_REFERENCE);
      }
      if (
        rollup.capexPerKwUsd != null
        && canWrite(phase.phaseId, 'capexPerKwUsd', orig?.capexPerKwUsd ?? null)
      ) {
        next.capexPerKwUsd = rollup.capexPerKwUsd;
        stamp(phase.phaseId, 'capexPerKwUsd', 'bom-rollup', BOM_ROLLUP_CELL_REFERENCE);
      }
    } else {
      // No publishable rollup — leave capex blank, record it as unsourced.
      if (next.capexTotalUsd == null) {
        skippedUnsourced.push({ phaseId: phase.phaseId, field: 'capexTotalUsd' });
      }
    }

    return next;
  });

  return { phases, fieldProvenance, filledFields, skippedUnsourced };
}

// ─────────────────────────────────────────────────────────────────────────────
// F4 / 1.7 — Project Intent offtake price → FA PPA price.
//
// `ProjectIntent.offtake.pricePerMwhUsd` is documented as feeding
// `financialAssumptions.ppaPricePerMwhUsd`, but nothing wrote it. An FA with no
// PPA price makes `computeYearOneRevenueUsd` null ⇒ NPV null ⇒ the Financial
// Viability verdict is WITHHELD, even though the operator DECLARED the price on
// intent. This closes that gap as a strict GAP-FILL: a user `manual` (or
// extracted / baseline) value is never overwritten.
// ─────────────────────────────────────────────────────────────────────────────

/** Provenance cell reference for the intent-declared offtake price. */
export const INTENT_OFFTAKE_CELL_REFERENCE = 'projectIntent.offtake.pricePerMwhUsd';

/** The one field of `ProjectIntent.offtake` this gap-fill reads. */
export interface IntentOfftakePriceSource {
  pricePerMwhUsd?: number | null;
}

export interface IntentOfftakeGapFillResult {
  next: FinancialAssumptions;
  /** True when `ppaPricePerMwhUsd` was actually written this run. */
  filled: boolean;
}

/**
 * Gap-fill `ppaPricePerMwhUsd` from the intent-declared offtake price.
 *
 * Writes ONLY when the FA field is blank, or when it carries a `'derived'`
 * stamp (i.e. this same gap-fill wrote it before and the declaration has since
 * moved). A `'manual'`, `'industry-baseline'` or any `extracted-*` stamp is
 * PROTECTED — mirrors the per-phase `canWrite` rule in
 * `buildFinancialPullFromSources`, so the two sources of the same pull obey one
 * protection rule.
 *
 * Pure; does not mutate the input.
 */
export function applyIntentOfftakeGapFill(
  fa: FinancialAssumptions,
  offtake: IntentOfftakePriceSource | null | undefined,
  now: string,
): IntentOfftakeGapFillResult {
  const declared = offtake?.pricePerMwhUsd;
  if (declared == null || !Number.isFinite(declared) || declared <= 0) {
    return { next: fa, filled: false };
  }
  const current = fa.ppaPricePerMwhUsd;
  const prov = fa.fieldProvenance?.ppaPricePerMwhUsd;
  const writable = current == null || prov?.source === 'derived';
  if (!writable || current === declared) return { next: fa, filled: false };

  return {
    next: {
      ...fa,
      ppaPricePerMwhUsd: declared,
      fieldProvenance: {
        ...fa.fieldProvenance,
        ppaPricePerMwhUsd: {
          source: 'derived',
          confidence: 'medium',
          cellReference: INTENT_OFFTAKE_CELL_REFERENCE,
          extractedAt: now,
        },
      },
    },
    filled: true,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// A4 — CAPEX conservation guard (Wave A keystone).
//
// WHY: the per-phase rollup (`computePerPhaseCapexFromBom`) can SILENTLY drop
// CAPEX in two ways: (1) an item whose `phaseId` matches no current phase is
// `continue`'d around (orphan); (2) a phase with priced items but no positive
// capacity is left unpublishable. Either way Σ(per-phase capexTotalUsd) drifts
// below `itemizedCapexFromBom(plan).total`, and the financial model under-counts
// real spend with NO warning. This guard reconciles the two totals and returns
// the gap + the orphaned lines as STRUCTURED warnings — it NEVER throws (the
// prod report path must never block on a data-quality issue), so callers log /
// surface the warnings and proceed with the best available numbers.
// ─────────────────────────────────────────────────────────────────────────────

/** One orphaned BoM line — `phaseId` set but matching no current phase. */
export interface OrphanedBomItemWarning {
  bomItemId: string;
  /** The phaseId the line references but which is absent from the phase set. */
  phaseId: string;
  /** The line's priced cost (USD), or null when unpriced. */
  totalCostUsd: number | null;
}

export interface CapexConservationResult {
  /** Σ of published per-phase `capexTotalUsd` (the financial model's basis). */
  perPhaseTotalUsd: number;
  /** `itemizedCapexFromBom(plan).total` — every priced line, phase-agnostic. */
  itemizedTotalUsd: number;
  /** itemizedTotalUsd − perPhaseTotalUsd. > 0 ⇒ CAPEX dropped from the rollup. */
  unreconciledUsd: number;
  /** True when |unreconciledUsd| is within tolerance of zero. */
  reconciles: boolean;
  /** Priced cost (USD) attributable to orphaned-phaseId lines. */
  orphanedCostUsd: number;
  /** Priced cost (USD) sitting in phases that were left unpublishable. */
  unpublishablePhaseCostUsd: number;
  /** Per-line detail for every orphaned-phaseId item (warning, not error). */
  orphanedItems: OrphanedBomItemWarning[];
  /** Human-readable warnings for logs / the variance surface. */
  warnings: string[];
}

/**
 * A4 — reconcile Σ(per-phase CAPEX) against the itemized BoM total and surface
 * any CAPEX the per-phase rollup dropped (orphaned phaseId, unpublishable phase).
 *
 * Tolerance: an absolute 1¢ floor OR 1e-6 of the itemized total (whichever is
 * larger) absorbs floating-point summation noise without masking a real gap.
 * Pure; never throws.
 */
export function checkCapexConservation(
  plan: SupplyChainPlan | null | undefined,
  phases: readonly FinancialPhase[],
): CapexConservationResult {
  const itemized = itemizedCapexFromBom(plan);
  const itemizedTotalUsd = itemized.total;

  const rollups = computePerPhaseCapexFromBom(plan, phases);
  const perPhaseTotalUsd = rollups.reduce(
    (sum, r) => sum + (r.capexTotalUsd != null && Number.isFinite(r.capexTotalUsd) ? r.capexTotalUsd : 0),
    0,
  );

  // Classify dropped CAPEX. Items whose phaseId matches no phase are orphaned;
  // priced items in a phase the rollup couldn't publish (no capacity) are the
  // other leak. Mirror the bucketing rule in computePerPhaseCapexFromBom.
  const knownPhaseIds = new Set(phases.map((p) => p.phaseId));
  const publishedPhaseIds = new Set(
    rollups.filter((r) => r.capexTotalUsd != null).map((r) => r.phaseId),
  );
  const items: BomItem[] = Array.isArray(plan?.bom) ? plan!.bom : [];

  const orphanedItems: OrphanedBomItemWarning[] = [];
  let orphanedCostUsd = 0;
  let unpublishablePhaseCostUsd = 0;
  for (const item of items) {
    const priced = item.totalCostUsd != null && Number.isFinite(item.totalCostUsd)
      ? (item.totalCostUsd as number)
      : null;
    // Orphan: phaseId is set AND references a phase not in the set.
    if (item.phaseId != null && !knownPhaseIds.has(item.phaseId)) {
      orphanedItems.push({ bomItemId: item.id, phaseId: item.phaseId, totalCostUsd: priced });
      if (priced != null) orphanedCostUsd += priced;
      continue;
    }
    // Unpublishable: attributable to a KNOWN phase that produced no published
    // capex (e.g. the phase has priced items but no positive capacity).
    if (priced != null && item.phaseId != null && !publishedPhaseIds.has(item.phaseId)) {
      unpublishablePhaseCostUsd += priced;
    }
  }

  const unreconciledUsd = itemizedTotalUsd - perPhaseTotalUsd;
  const tolerance = Math.max(0.01, Math.abs(itemizedTotalUsd) * 1e-6);
  const reconciles = Math.abs(unreconciledUsd) <= tolerance;

  const warnings: string[] = [];
  if (orphanedItems.length > 0) {
    warnings.push(
      `${orphanedItems.length} BoM line(s) reference a phase id that no longer exists ` +
        `(orphaned), dropping $${Math.round(orphanedCostUsd).toLocaleString()} of CAPEX from the ` +
        `per-phase financial model. Re-assign or remove these lines.`,
    );
  }
  if (unpublishablePhaseCostUsd > tolerance) {
    warnings.push(
      `$${Math.round(unpublishablePhaseCostUsd).toLocaleString()} of priced CAPEX sits in phase(s) ` +
        `with no capacity, so it is excluded from the per-phase rollup. Set the phase capacity to ` +
        `publish it.`,
    );
  }
  if (!reconciles && warnings.length === 0) {
    // A residual gap with no identified cause — flag it generically so it is
    // never silent (the whole point of A4).
    warnings.push(
      `Per-phase CAPEX ($${Math.round(perPhaseTotalUsd).toLocaleString()}) does not reconcile with ` +
        `the itemized BoM total ($${Math.round(itemizedTotalUsd).toLocaleString()}); ` +
        `$${Math.round(Math.abs(unreconciledUsd)).toLocaleString()} unaccounted.`,
    );
  }

  return {
    perPhaseTotalUsd,
    itemizedTotalUsd,
    unreconciledUsd,
    reconciles,
    orphanedCostUsd,
    unpublishablePhaseCostUsd,
    orphanedItems,
    warnings,
  };
}
